import { useCallback, useId, useMemo } from "react";
import {
  IconNameEnum,
  useModuleBEM,
  useUiStyles,
} from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import type {
  LocationAutocompleteProps as CoreLocationAutocompleteProps,
  LocationSuggestion,
} from "@b2bportal/core-ui";
import { isWebView } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { InputAdornment, TextField } from "@material-ui/core";
import {
  type AutocompleteRenderGroupParams,
  type AutocompleteRenderInputParams,
  type AutocompleteRenderOptionState,
  Autocomplete as MuiAutocomplete,
} from "@material-ui/lab";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
  Loading,
  useLocationAutocomplete,
} from "@tenant/ui";

import clsx from "clsx";
import defaultStyles from "./LocationAutocomplete.module.scss";
interface LocationAutocompleteProps<T>
  extends CoreLocationAutocompleteProps<T> {
  density?: "default" | "search";
  mobileDialogTitle?: string;
}

export const LocationAutocomplete = <T extends LocationSuggestion>(
  props: LocationAutocompleteProps<T>
) => {
  const {
    id,
    defaultValue,
    label,
    disabled,
    groupBy,
    getOptionLabel,
    getGroupLabelKey,
    getOptionIcon,
    icon,
    paperClassName,
    placeholder,
    useDialogOnMobile = false,
    mobileDialogTitle,
    topContent,
    error,
    density = "search",
  } = props;

  const COMPONENT_KEY = CoreUiComponents.LocationAutocomplete;
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY, {
    error: error != null,
  });

  const className = clsx(
    props.className,
    cn(density, { error: error != null })
  );
  const isInWebView = useMemo(() => isWebView(), []);
  const errorId = useId();

  const { t } = useI18nContext();

  const {
    context: { open, loading, shrink, value, search, options, dialogProps },
    handlers: {
      handleOnBlur,
      handleOnClose,
      handleOnFocus,
      handleOnInputChange,
      handleOnOpen,
      handleOnChangeValue,
    },
  } = useLocationAutocomplete<T>(props);

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams, autoFocus = false) => (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder}
        variant="filled"
        error={error != null}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- Autofocus improves UX when opening search dialog on mobile
        autoFocus={autoFocus}
        InputLabelProps={{
          shrink: shrink || !!value || placeholder != null,
          focused: false,
          className: cn("label"),
        }}
        inputProps={{
          ...params.inputProps,
          "aria-required": "true",
        }}
        InputProps={{
          ...params.InputProps,
          className: cn("input"),
          disableUnderline: true,
          "aria-describedby": error != null ? errorId : undefined,
          startAdornment: (
            <>
              <InputAdornment position="start">
                {icon ? (
                  icon
                ) : (
                  <Icon
                    className={cn("input-icon")}
                    iconName={IconNameEnum.mapPin}
                  />
                )}
              </InputAdornment>
              {params.InputProps.startAdornment}
            </>
          ),
          endAdornment: (
            <>
              <InputAdornment position="end" className={cn("end-icons")}>
                {loading ? <Loading className={cn("loading-icon")} /> : null}
              </InputAdornment>
              {params.InputProps.endAdornment}
            </>
          ),
        }}
        helperText={
          <VisuallyHidden asChild>
            <span id={errorId} role="alert">
              {t("requiredField")}
            </span>
          </VisuallyHidden>
        }
      />
    ),
    [label, placeholder, error, shrink, value, cn, errorId, icon, loading, t]
  );

  const renderGroup = useCallback(
    ({ key, group, children }: AutocompleteRenderGroupParams) => {
      const renderTopContent = () => {
        // key type is not correct, its a number
        // eslint-disable-next-line eqeqeq
        if (topContent != null && key == "0") {
          return <div className={cn("top-content")}>{topContent}</div>;
        }
        return null;
      };
      const groupTitle = getGroupLabelKey ? getGroupLabelKey(group) : undefined;
      return (
        <>
          {renderTopContent()}
          {groupTitle != null ? (
            <div className={cn("group-title")}>{groupTitle}</div>
          ) : null}
          {children}
        </>
      );
    },
    [getGroupLabelKey, cn, topContent]
  );

  const renderOption = useCallback(
    (option: T, state: AutocompleteRenderOptionState) => {
      const icon = getOptionIcon ? getOptionIcon(option) : undefined;
      return (
        <div className={cn("location-item", { icon: !!icon })}>
          {icon ? <div className={cn("location-icon")}>{icon}</div> : null}
          {option.label}
        </div>
      );
    },
    [cn, getOptionIcon]
  );

  const autocomplete = (
    internalClassname: string,
    disablePortal: boolean,
    autoFocus = false,
    isDialog = false
  ) => (
    <MuiAutocomplete
      id={id}
      open={open}
      disabled={disabled}
      onOpen={handleOnOpen}
      // In the mobile dialog, MUI Autocomplete fires onClose with reason "blur"
      // whenever focus leaves the input (e.g. virtual keyboard dismiss, tab switch).
      // This would clear the results list, which is undesirable in a full-screen dialog
      // where the dialog's own close button controls dismissal. MUI has no built-in prop
      // to prevent this, so we filter the "blur" reason using the controlled open state.
      // See onClose reason values: https://v4.mui.com/api/autocomplete/#props
      onClose={
        isDialog
          ? (_e, reason) => {
              if (reason !== "blur") handleOnClose();
            }
          : handleOnClose
      }
      value={value}
      inputValue={search}
      defaultValue={defaultValue}
      options={options}
      loading={loading}
      loadingText={t?.("loading")}
      noOptionsText={t?.("noOptions")}
      classes={{
        paper: clsx(cn("paper"), paperClassName),
      }}
      onChange={handleOnChangeValue}
      onInputChange={handleOnInputChange}
      groupBy={groupBy}
      getOptionLabel={getOptionLabel}
      disableClearable={loading}
      clearOnBlur={!isDialog}
      disablePortal={disablePortal}
      onFocus={handleOnFocus}
      onBlur={isDialog ? undefined : handleOnBlur}
      renderInput={(params) => renderInput(params, autoFocus)}
      renderGroup={renderGroup}
      renderOption={renderOption}
      closeIcon={
        <Icon iconName={IconNameEnum.close} className={cn("close-icon")} />
      }
      className={clsx(block, className, internalClassname)}
      // Disable UI filtering
      filterOptions={(options: T[]) => options}
    />
  );

  const onLocationButtonClick = useCallback(() => {
    handleOnFocus();
  }, [handleOnFocus]);

  return (
    <>
      <div>
        {autocomplete(cn("input", { show: !useDialogOnMobile }), false, false)}
      </div>
      {/* modal={true} on mobile web enables Radix's body scroll lock, preventing
          the page from scrolling behind the full-screen dialog. In WebView, modal
          stays false (the original behavior from PR #15502) because the native app
          manages scroll via useMobileAppScrollFix. */}
      <Dialog modal={!isInWebView} {...dialogProps.props}>
        <DialogTrigger asChild>
          <button
            onClick={onLocationButtonClick}
            onFocus={handleOnFocus}
            aria-describedby={error != null ? errorId : undefined}
            className={clsx(
              cn("location-button", {
                show: useDialogOnMobile,
              }),
              className
            )}
          >
            <div className={cn("icon")}>
              <Icon iconName={IconNameEnum.mapPin} />
            </div>
            <div className={cn("content")}>
              <div
                className={cn("location-button-label", {
                  filled: value != null,
                })}
              >
                {label}
              </div>
              <div
                className={cn("location-button-placeholder", {
                  filled: value != null,
                })}
              >
                {placeholder}
              </div>
              <div className={cn("location-button-value")}>
                {value != null ? getOptionLabel?.(value) : ""}
              </div>
            </div>
          </button>
        </DialogTrigger>
        <DialogContent
          fullScreen={true}
          title={mobileDialogTitle ?? label}
          className={cn("dialog")}
        >
          {autocomplete(cn("dialog-input"), true, true, true)}
        </DialogContent>
      </Dialog>
      {error != null && useDialogOnMobile && (
        <VisuallyHidden asChild>
          <div id={errorId} role="alert">
            {t("requiredField")}
          </div>
        </VisuallyHidden>
      )}
    </>
  );
};
